<template>
  <div class="player">
    <OSDHeader
      :is-show-osd="isOSDActive"
      :is-back-active="getIsBackButtonActive"
      @hide="() => (isOSDActive = false)"
    />
    <ViewerImage v-if="isImage" :src="src" />
    <VideoPlayer
      v-if="isVideo"
      ref="videoPlayer"
      :src="src"
      :seek-to="seekTo"
      :title="title"
      :description="description"
      :value="currentPosition"
      :status="videoStatus"
      :autoplay="autoplay"
      :loop="loop"
      :controls="controls"
      :is-osd-visible="isOSDActive"
      @status="handleVideoStatus"
      @input="updatePlayPosition"
      @setVideo="assignVideoObject"
      @ended="handleVideoEnded"
    />
    <WebViewer v-if="isWebsite" :src="src" :scroll-position="scrollPosition" />
    <!-- Notanzeige fuer einen unbekannten Typ.
         Vorher stand hier ein v-else zum WebViewer - damit erschien die
         Tabelle bei JEDEM Typ ausser WEBSITE, also auch unter dem laufenden
         Video und unter jedem Bild. Auf dem Schirm faellt das kaum auf (sie
         steht unterhalb der bildschirmhohen Videoflaeche), die Seite wird
         dadurch aber scrollbar. Gezeigt wird sie jetzt nur noch, wenn wirklich
         keiner der drei Typen greift - dafuer ist sie gedacht, wie die Spalte
         "Allowed values" zeigt. -->
    <div v-if="isUnknownType">
      <table>
        <tr>
          <th>Key</th>
          <th>Value</th>
          <th>Allowed values</th>
        </tr>

        <tr>
          <td>type</td>
          <td>{{ type }}</td>
          <td>VIDEO, IMAGE, WEBSITE</td>
        </tr>
        <tr>
          <td>src</td>
          <td>{{ src }}</td>
        </tr>
        <tr>
          <td>title</td>
          <td>{{ title }}</td>
        </tr>
        <tr>
          <td>description</td>
          <td>{{ description }}</td>
        </tr>
        <tr>
          <td>seekTo</td>
          <td>{{ seekTo }}</td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import ViewerImage from '@/components/ViewerImage.vue';
import VideoPlayer, { VideoStates } from '@/components/VideoPlayer';
import WebViewer from '@/components/WebViewer.vue';
import { keyHandler, KEYS } from '@/mixins/keyHandler';
import router from '@/router';
import { MEDIA_TYPE } from '@/helpers/MediaHandler';
import OSDHeader from '@/components/OSDHeader.vue';
import { NAV_TYPE } from '@/store/modules/navigation';

export default {
  mixins: [keyHandler],
  components: {
    ViewerImage,
    VideoPlayer,
    WebViewer,
    OSDHeader,
  },
  props: {
    type: String,
    src: String,
    seekTo: Number,
    title: String,
    description: String,
    autoplay: Boolean,
    loop: Boolean,
    controls: {
      type: Boolean,
      default: true,
    },
  },
  emits: {},
  data() {
    return {
      currentPosition: 0,
      videoStatus: null,
      scrollPosition: 0,
      disableBackButton: false,
      isButtonBackActive: false,
      isOSDActive: true,
      video: undefined,
    };
  },
  mounted() {
    if (this.isVideo) {
      this.videoStatus = VideoStates.PLAY;
    }
    if (this.isWebsite) {
      window.addEventListener('message', this.handleIncomingMessage);
    }
  },
  beforeDestroy() {
    this.disableBackButton = false;
    if (this.isWebsite) {
      window.removeEventListener('message', this.handleIncomingMessage);
    }
  },
  methods: {
    assignVideoObject(video) {
      this.video = video;
    },
    handleIncomingMessage(e) {
      if (e.message === 'disable-back-button') {
        this.disableBackButton = true;
      }
    },
    updatePlayPosition(value) {
      this.currentPosition = value;
    },
    handleVideoStatus(status) {
      if (status !== this.videoStatus) {
        this.videoStatus = status;
      }
    },
    eventHandler(key, event) {
      this.isOSDActive = true;
      this.$eventBus.$emit('key-action', { key, event });
      if (this.isVideo) {
        this.videoKeyHandler(key);
      }
      if (this.isWebsite) {
        this.webKeyHandler(key, event);
      }
      if (this.isImage) {
        this.imageKeyHandler(key, event);
      }
    },
    /**
     * Tasten des VoD-Players.
     *
     * Bedient werden die Wiedergabetasten der Fernbedienung (Play, Pause, Stop,
     * Vor- und Rueckspulen) sowie OK und LINKS/RECHTS. Ausgefuehrt wird alles
     * ueber die Methoden der VideoPlayer-Komponente, damit die Zustandslogik
     * (Tempostufen, Ende der Datei) an EINER Stelle liegt - in
     * helpers/VodPlayer.
     *
     * Ohne controls darf der Gast nicht eingreifen (erzwungene Einspielungen
     * ueber den Socket, siehe MediaHandler.assembleSocketVideoQuery). Dann
     * bleibt nur BACK.
     */
    videoKeyHandler(key) {
      const player = this.$refs.videoPlayer;

      // Steht der Zurueck-Knopf im Fokus, ist OK ein Klick DARAUF und kein
      // Play/Pause. Vorher lief beides nacheinander - die Seite wurde
      // verlassen UND die Wiedergabe umgeschaltet.
      //
      // Vor der controls-Abfrage, denn ohne controls gilt der Knopf immer als
      // im Fokus (siehe getIsBackButtonActive) - OK ist dort neben BACK der
      // einzige Weg heraus.
      if (key === KEYS.ENTER && this.getIsBackButtonActive) {
        this.$store.dispatch('navigation/to', NAV_TYPE.BACK);
        return;
      }

      if (!player || !this.controls) {
        this.handleGlobalKeys(key);
        return;
      }

      switch (key) {
        case KEYS.ENTER:
          player.togglePlayPause();
          break;
        case KEYS.MEDIA_PLAY:
          player.play();
          break;
        case KEYS.MEDIA_PAUSE:
          player.pause();
          break;
        case KEYS.MEDIA_PLAY_PAUSE:
          player.togglePlayPause();
          break;
        case KEYS.MEDIA_STOP:
          // Anhalten und auf den Anfang zurueck - die Seite bleibt stehen.
          // Bewusst KEIN Ruecksprung in die Liste: der Gast kann mit Play
          // wieder von vorn beginnen, und zum Verlassen gibt es BACK.
          player.stop();
          break;
        case KEYS.MEDIA_FAST_FORWARD:
          player.fastForward();
          break;
        case KEYS.MEDIA_REWIND:
          player.rewind();
          break;
        case KEYS.RIGHT:
          player.stepForward();
          break;
        case KEYS.LEFT:
          player.stepBackward();
          break;
        case KEYS.UP:
          this.isButtonBackActive = true;
          break;
        case KEYS.DOWN:
          this.isButtonBackActive = false;
          break;
      }
      this.handleGlobalKeys(key);
    },
    /**
     * Datei zu Ende: zurueck in die Liste.
     *
     * Nur fuer den VoD-Fall (controls) und nicht in der Dauerschleife. Bei
     * erzwungenen Einspielungen ueber den Socket bleibt es wie bisher beim
     * stehenden letzten Bild - dort entscheidet die next_action, was folgt.
     */
    handleVideoEnded() {
      if (this.loop || !this.controls) return;
      this.$store.dispatch('navigation/to', NAV_TYPE.BACK);
    },
    webKeyHandler(key, event) {
      this.$eventBus.$emit('key-handler', { key, event });
      switch (key) {
        case KEYS.ENTER:
          if (this.getIsBackButtonActive) {
            this.$store.dispatch('navigation/to', NAV_TYPE.BACK);
          }
          break;
        case KEYS.UP:
          this.isButtonBackActive = true;
          break;
        case KEYS.DOWN:
          this.isButtonBackActive = false;
          break;
      }
      if (!this.disableBackButton) this.handleGlobalKeys(key);
    },
    imageKeyHandler(key, event) {
      switch (key) {
        case KEYS.BACK:
        case KEYS.ENTER:
          this.$store.dispatch('navigation/to', NAV_TYPE.BACK);
          break;
      }
    },
    handleGlobalKeys(key, event) {
      switch (key) {
        case KEYS.BACK:
          this.$store.dispatch('navigation/to', NAV_TYPE.BACK);
          break;
      }
    },
  },
  computed: {
    getId() {
      return this.$route.params.id;
    },
    isImage() {
      return this.type === MEDIA_TYPE.IMAGE;
    },
    isVideo() {
      return this.type === MEDIA_TYPE.VIDEO;
    },
    isWebsite() {
      return this.type === MEDIA_TYPE.WEBSITE;
    },
    isUnknownType() {
      return !this.isImage && !this.isVideo && !this.isWebsite;
    },
    getIsBackButtonActive() {
      if (this.isImage) return true;
      if (!this.controls) return true;
      return this.isButtonBackActive;
    },
  },
};
</script>

<style scoped>
.player--info {
  padding: 30px;
  text-align: left;
  margin: 0 auto;
  width: 50%;
}
.player--info td,
.player--info th {
  padding: 0 1rem;
}
</style>



// WEBPACK FOOTER //
// src/pages/MediaPlayer.vue